import React from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";

// FineAnts' own platform Stripe account (charging entities for their
// FineAnts subscription) - distinct from each entity's own Stripe
// integration (collecting payments from THEIR clients), which uses a
// per-entity key fetched from the backend per-request, not this one.
//
// The publishable key is fetched from the backend at runtime, not read
// from a REACT_APP_ build env var - CRA inlines those at BUILD time, not
// serve time, so an env var added/changed after the last build (e.g. an
// Azure App Setting) would never actually reach the bundle. Mirrors how
// document-viewer.tsx already gets a per-entity Stripe key at runtime.
let cachedPromise: Promise<Stripe | null> | null = null;
let cachedKey: string | null = null;
const getPlatformStripePromise = (publishableKey: string) => {
    if (!cachedPromise || cachedKey !== publishableKey) {
        cachedKey = publishableKey;
        cachedPromise = loadStripe(publishableKey);
    }
    return cachedPromise;
};

// Any component mounting <Elements> for the platform checkout (the regular
// "Choose your plan" flow, or the interactive upgrade-approval flow a
// blocked feature routes into) calls this instead of duplicating the
// fetch-then-loadStripe dance - the key and the loaded Stripe.js instance
// are both cached at module scope, so re-mounting this hook elsewhere in
// the app reuses the same one rather than fetching or loading twice.
export const usePlatformStripe = () => {
    const [result, setFetch] = useApi<{ publishableKey: string | null }>();

    React.useEffect(() => {
        setFetch(buildApiRequestConfig(routes.subscription.platformStripeKey));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const publishableKey = result.data?.publishableKey ?? null;

    return {
        stripePromise: publishableKey ? getPlatformStripePromise(publishableKey) : null,
        isLoading: result.isLoading || (!result.data && !result.error),
        // Only a real "this is broken" case, not the normal still-loading state.
        error: result.data && !publishableKey ? 'Payments are not configured yet.' : '',
    };
};

// Confirms the PaymentIntent already attached to the enclosing <Elements>
// (via its clientSecret) - shared by the regular checkout
// (subscription-plans.tsx's PaidCheckoutStep) and the interactive
// upgrade-approval flow (upgrade-required-dialog.tsx), which both need the
// exact same confirm/error/already-processing handling. Must be called from
// a component rendered inside <Elements> - useStripe/useElements throw
// otherwise.
export const useConfirmStripePayment = (onSuccess: (paymentIntentId: string) => void) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = React.useState(false);
    const [error, setError] = React.useState('');

    const confirm = async () => {
        if (!stripe || !elements) return;
        setProcessing(true);
        setError('');
        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        if (confirmError) {
            setError(confirmError.message || 'Payment failed. Please try again.');
            setProcessing(false);
            return;
        }
        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
            onSuccess(paymentIntent.id);
        } else {
            setProcessing(false);
        }
    };

    return { confirm, processing, error, ready: !!stripe && !!elements };
};
